/* eslint-disable no-await-in-loop */
// eslint-disable-next-line
import type { Handlers, Proxy } from '#~/agent/types.js';
import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import type { ByteId } from '#~/api/index.js';
import { AutoTrackingAnnotationManager } from './annotation-manager.js';
import { AutoTrackingStateManager } from './state-manager.js';
import { AutoTrackingSubscriptionManager } from './subscription-manager.js';
import type { Annotation } from '@studio/design-system/video-annotation';
import type { AutoTrackingReservation } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { pingSubjectForReservation } from './utils.js';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Subscription } from 'nats.ws';
import type { NewtsConnection } from '#~/newts/connection.js';
import { generateId } from '@studio/models';

/** A class to manage auto tracking reservations for an agent. */
export class AutoTrackingManager {
  readonly #proxy: Proxy;

  readonly #annotationManager: AutoTrackingAnnotationManager;
  readonly #reservationStateManager: AutoTrackingStateManager;
  readonly #reservationSubscriptionManager: AutoTrackingSubscriptionManager;

  /**
   * Creates a new auto tracking manager.
   *
   * @param conn - The nats connection to use.
   * @param agentId - The id of the agent to create the manager for.
   * @param agentSubordinate - The subordinate to sync agent state with.
   * @param handleAutoTrackingAnnotationMutation - The function to call when an annotation mutation
   *   is received.
   */
  public constructor(
    proxy: Proxy,
    handleAutoTrackingAnnotationMutation: Handlers['handleAutoTrackingAnnotationMutation']
  ) {
    this.#proxy = proxy;

    this.#reservationStateManager = new AutoTrackingStateManager(this.#proxy);

    this.#annotationManager = new AutoTrackingAnnotationManager(
      this.#proxy.id,
      this.#reservationStateManager,
      handleAutoTrackingAnnotationMutation
    );

    this.#reservationSubscriptionManager = new AutoTrackingSubscriptionManager(this.#proxy);
  }

  readonly #pendingReservations: Record<string, boolean> = {};
  readonly #existingReservations: Record<string, DeepPartial<AutoTrackingReservation>> = {};

  readonly #reservationPingSubscriptions: Record<string, Subscription> = {};

  /**
   * Create a new auto tracking reservation.
   *
   * @param cameraId - The id of the camera to create the reservation for.
   */
  public async createReservation(cameraId: ByteId): Promise<void> {
    if (this.#proxy.state.status?.device?.[toHexFromId(cameraId)!] === undefined) {
      // Device does not exist

      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            await this.createReservation(cameraId);
            resolve();
          } catch (e) {
            reject(e);
          }
        }, 500);
      });
    }

    if (this.#pendingReservations[toHexFromId(cameraId)!]) {
      return;
    }

    const existingReservation = Object.values(this.#existingReservations).find(
      (reservation) =>
        reservation.cameraId && toHexFromId(reservation.cameraId) === toHexFromId(cameraId)
    );

    // Check if we already have a reservation for this camera, if so we don't need to create a new one
    if (existingReservation) {
      // check if this reservation is still valid
      const res =
        this.#proxy.state.control?.studio?.autoTrackingReservation?.[
          toHexFromId(existingReservation.reservationId)!
        ];

      if (res !== undefined) {
        return;
      }
    }

    this.#pendingReservations[toHexFromId(cameraId)!] = true;

    // Create the reservation data representation for the information we have
    const reservation: DeepPartial<AutoTrackingReservation> = {
      agentId: toIdFromHex(this.#proxy.id)!,
      cameraId,
      reservationId: toIdFromHex(generateId())!,
      readyAt: Date.now()
    };

    let retryCount = 0;

    const subject = pingSubjectForReservation(
      toIdFromHex(this.#proxy.id)!,
      reservation.reservationId!
    );

    const subscription = this.#proxy.connection.subscribe(subject, {
      callback: (err, msg) => {
        if (err) {
          console.error('[Auto Tracking] Error receiving ping response for reservation', err);
          return;
        }

        msg.reply &&
          (this.#proxy.connection as NewtsConnection).publish(msg.reply, new Uint8Array([1]));
      }
    });

    this.#reservationPingSubscriptions[toHexFromId(reservation.cameraId)!] = subscription;

    while (retryCount < 5) {
      // Add the reservation to the map
      try {
        await this.#proxy.mutate(
          {
            control: {
              studio: {
                autoTrackingReservation: {
                  [toHexFromId(reservation.reservationId)!]: reservation
                }
              }
            }
          },
          {},
          false
        );

        this.#existingReservations[toHexFromId(cameraId)!] = reservation;

        break;
      } catch (e) {
        retryCount++;

        console.error('[Auto Tracking] Error creating reservation', e, retryCount);

        if (retryCount >= 5) {
          throw e;
        }

        continue;
      }
    }

    this.#pendingReservations[toHexFromId(cameraId)!] = false;
  }

  public async transferReservation(cameraId: ByteId): Promise<void> {
    let existingReservation = this.#existingReservations[toHexFromId(cameraId)!];

    if (!existingReservation) {
      throw new Error('No existing reservation to transfer');
    }

    await this.#proxy.mutate(
      {
        control: {
          studio: {
            autoTrackingReservation: {
              [toHexFromId(existingReservation.reservationId)!]: {
                cameraId: existingReservation.cameraId,
                transferPriority: +Date.now(),
                reservationId: existingReservation.reservationId
              }
            }
          }
        }
      },
      {},
      false
    );
  }

  /**
   * Destroy a reservation.
   *
   * @param cameraIdBytes - The id of the camera to destroy the reservation for.
   */
  public async destroyReservation(cameraIdBytes: ByteId): Promise<void> {
    const cameraId = toHexFromId(cameraIdBytes)!;

    if (this.#pendingReservations[cameraId]) {
      return;
    }

    this.#pendingReservations[cameraId] = true;

    const subscription = this.#reservationPingSubscriptions[cameraId];

    if (subscription) {
      subscription.unsubscribe();
      delete this.#reservationPingSubscriptions[cameraId];
    }

    const existingReservation = this.#existingReservations[cameraId];

    delete this.#existingReservations[cameraId];

    // Remove the reservation from the map
    try {
      await this.#proxy.mutate(
        {},
        {
          control: {
            studio: {
              autoTrackingReservation: {
                [toHexFromId(existingReservation?.reservationId)!]: {}
              }
            }
          }
        },
        false
      );
    } catch (e) {
      console.error('[Auto Tracking] Error destroying reservation', e);
    }

    this.#pendingReservations[cameraId] = false;
  }

  /**
   * Gets the annotations for a camera.
   *
   * @param cameraId - The id of the camera to get the annotations for.
   * @returns The annotations for the camera.
   */
  public getAnnotationsForCameraId(cameraId: ByteId): Annotation[] {
    return this.#annotationManager.getAnnotationsForCameraId(cameraId);
  }

  /**
   * When a mutation is made to the reservation state, we need to ensure we have the correct
   * subscriptions.
   *
   * @param reservations - The reservations to ensure subscriptions for.
   */
  public handleMutation(reservations: Record<string, AutoTrackingReservation>): void {
    this.#reservationSubscriptionManager.checkSubscriptions(reservations);
    this.#annotationManager.checkNotificationSubscriptions(reservations);
  }
}
